import { useState } from "react"
import Button from "@/components/Button/Button"
import CopyIcon from "@/components/CopyIcon/CopyIcon"
import Modal from "@/components/Modal/Modal"
import Stepper from "@/components/Stepper/Stepper"
import Toggle from "@/components/Toggle/Toggle"
import FamilyPicker from "@/components/SessionRules/FamilyPicker"
import { NAV_COPY } from "@/content/rounds"
import {
  exercisesFor,
  handCapFormat,
  roundsSummary,
  steppersFor,
  stepperHint,
} from "@/content/sessionRules"
import { useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import { setFamilyInPlay, updateSessionConfig } from "@/services/sessions"
import { useSessionStore } from "@/store/sessionStore"
import { selectSession } from "@/store/selectors"
import { joinUrl } from "@/utils/routePaths"
import type { SessionConfig } from "@/types"
import styles from "@/styles/components/SessionSettings.module.css"

/*
 * The lobby's rules, reachable again mid-session.
 *
 * The same controls, writing the same config keys — deliberately, because a
 * facilitator who realises in the Cards round that five cards each is too many
 * should change the rule rather than work around it.
 *
 * What it does NOT offer is the template picker or the known pain points. A
 * template rewrites every round's length including ones already played, and the
 * loaded notes have by then been turned into pain documents that the room may
 * have voted on — editing the configuration they came from would do nothing
 * visible and imply otherwise.
 *
 * Archiving and deleting a session are NOT here either, deliberately: inside a
 * running room they are a destructive press one misclick from ending the
 * workshop, so they live on the session list and the customer screen instead,
 * away from the controls the facilitator is operating mid-session.
 */

const SETTINGS_COPY = {
  title: "Session settings",
  /* Said plainly: a round already played does not un-play because its length
   * changed, and the cap the room already picked against still stands. */
  scope: "Changes apply to the rounds still ahead.",
  done: "Done",
} as const

/** Said in place of the toggle's own hint, where the toggle cannot be used. */
const DEEP_IN_PROGRESS = "The room is in the deep dive. Move on to leave it."

/** How long the copy button reads "copied" before resetting. */
const COPIED_FOR_MS = 1800

interface SessionSettingsProps {
  onClose: () => void
}

export default function SessionSettings({ onClose }: SessionSettingsProps) {
  const session = useSessionStore(selectSession)
  const { write, isPending } = useFirestoreWrite()
  const [openFamilies, setOpenFamilies] = useState(false)
  const [copied, setCopied] = useState(false)

  if (!session) return null

  const config = session.config

  // The share link IS the session id — the join URL the lobby hands out, kept here
  // so the facilitator can re-send it to gather the room for a follow-up call.
  const shareLink = joinUrl(session.id)
  const copyLink = () => {
    void navigator.clipboard?.writeText(shareLink).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), COPIED_FOR_MS)
    })
  }
  /*
   * Only the deep-dive toggle is keyed. It adds or drops a whole round from a
   * session the room is standing in, so it is worth waiting for; the steppers
   * and the family checkboxes are the same high-frequency adjustments they are in
   * the lobby panel, and a spinner per press there is noise — see `SessionRules`.
   */
  const change = (patch: Partial<SessionConfig>, key?: string) =>
    write(() => updateSessionConfig(session.id, patch), key)

  return (
    <Modal
      title={SETTINGS_COPY.title}
      description={roundsSummary(config)}
      width="narrow"
      dismissible
      onClose={onClose}
      actions={
        <Button type="button" onClick={onClose}>
          {SETTINGS_COPY.done}
        </Button>
      }
    >
      <p className={styles.scope}>{SETTINGS_COPY.scope}</p>

      {steppersFor(config).map((stepper) => (
        <Stepper
          key={stepper.key}
          label={stepper.label}
          hint={stepperHint(stepper, config)}
          value={config[stepper.key]}
          min={stepper.min}
          max={stepper.max}
          format={stepper.key === "handCap" ? handCapFormat : undefined}
          onChange={(value) => change({ [stepper.key]: value })}
        />
      ))}

      {/*
       * Only the deep dive is still switchable. The pains round has either
       * already run or already been skipped by the time this dialog is
       * reachable, and the user-story form is what the job was written in.
       *
       * And not even the deep dive once the room is IN it. Switching it off
       * there takes the current round out of `activeRounds`, so `nextPosition`
       * can no longer find where the room is and answers null — which the round
       * controls read as "nothing left", completing the session on the spot:
       * no job gate, no wrap, the open job never closed.
       */}
      {exercisesFor()
        .filter((exercise) => exercise.key === "deepDiveEnabled")
        .map((exercise) => (
          <Toggle
            key={exercise.key}
            label={exercise.label}
            hint={session.round === "deep" ? DEEP_IN_PROGRESS : exercise.hint}
            checked={config[exercise.key]}
            disabled={session.round === "deep"}
            busy={isPending(exercise.key)}
            onChange={(checked) => change({ [exercise.key]: checked }, exercise.key)}
          />
        ))}

      {/* `lockLast`: the room is mid-session, and unticking the last family
          leaves the deal with nothing to hand out — an empty swipe deck and a
          Continue blocked on picks nobody can make. */}
      <FamilyPicker
        config={config}
        open={openFamilies}
        onToggle={() => setOpenFamilies((value) => !value)}
        onPick={(family, inPlay) => write(() => setFamilyInPlay(session.id, family, inPlay))}
        lockLast
      />

      {/* The join link, at the foot of the panel: one tap to copy and re-send when
          a saved session is picked up again. */}
      <div className={styles.shareRow}>
        <span className={styles.shareLabel}>{NAV_COPY.shareLinkLabel}</span>
        <div className={styles.shareLink}>
          <span className={styles.shareUrl}>{shareLink.replace(/^https?:\/\//, "")}</span>
          <button
            type="button"
            className={styles.shareCopy}
            title={copied ? NAV_COPY.copiedLink : NAV_COPY.copyLink}
            aria-label={copied ? NAV_COPY.copiedLink : NAV_COPY.copyLink}
            onClick={copyLink}
          >
            <CopyIcon done={copied} />
          </button>
        </div>
      </div>
    </Modal>
  )
}
